import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/daimt1bp14a1h3mr77a0";

type Status = "idle" | "submitting" | "success" | "error";

export default function SubscribeCta() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honey = String(formData.get("company_alt") ?? "").trim();
    if (honey) {
      setStatus("success");
      setFormError("");
      return;
    }
    formData.delete("company_alt");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = responseText.toLowerCase().includes("spam") || String(serverMsg).includes("spam") || String(serverMsg).includes("form data is spam");
      const succeeded = response.ok && parsed?.code === "OK" && !isSpam;

      if (succeeded) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(String(serverMsg) || "提交失败，请稍后重试");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查后重试");
    }
  };

  return (
    <section id="subscribe" className="w-full border-b border-background-200 bg-foreground-950 py-10 md:py-14">
      <div className="mx-auto max-w-[1400px] px-4 md:px-6">
        <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-2 rounded-full border border-background-50/20 px-3 py-1.5 text-xs font-semibold text-accent-400">
              <i className="ri-notification-3-line w-4 h-4 flex items-center justify-center"></i>
              风险预警订阅
            </span>
            <h3 className="mt-5 font-heading text-2xl font-black leading-tight text-background-50 md:text-3xl">
              在官宣之前，
              <br className="hidden sm:block" />
              先把合作名单过一遍。
            </h3>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-background-300">
              订阅后我们会按你关注的合作方向推送风险提醒：代言翻车、解约切割、公关危机节点与商业价值波动。
              当涉及你的品牌或正在洽谈的代言时，提醒会提前一到两周到达。
            </p>
            <ul className="mt-6 space-y-3">
              {["关注合作前的背景复核清单", "官宣 / 续约节点的提前提醒", "解约与切割动作的节奏跟踪"].map((t) => (
                <li key={t} className="flex items-center gap-2.5 text-sm text-background-200">
                  <i className="ri-checkbox-circle-fill w-5 h-5 flex items-center justify-center text-accent-500"></i>
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-lg border border-background-50/10 bg-background-50 p-5 md:p-7">
              {status === "success" ? (
                <div className="flex flex-col items-center py-10 text-center">
                  <i className="ri-checkbox-circle-fill w-14 h-14 flex items-center justify-center text-5xl text-secondary-500"></i>
                  <h4 className="mt-4 font-heading text-lg font-bold text-foreground-950">订阅成功</h4>
                  <p className="mt-2 max-w-sm text-sm text-foreground-600">
                    我们已收到你的关注方向。有与你相关的合作风险线索时，会优先推送到你留下的联系方式。
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="mt-6 cursor-pointer rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-foreground-400"
                  >
                    再订阅一个方向
                  </button>
                </div>
              ) : (
                <form id="risk-subscribe-form" data-readdy-form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="sub-name" className="mb-1.5 block text-xs font-semibold text-foreground-700">
                        称呼 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="sub-name"
                        name="name"
                        type="text"
                        required
                        placeholder="如何称呼你"
                        className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                      />
                    </div>
                    <div>
                      <label htmlFor="sub-email" className="mb-1.5 block text-xs font-semibold text-foreground-700">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="sub-email"
                        name="email"
                        type="email"
                        required
                        placeholder="name@brand.com"
                        className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                      />
                    </div>
                    <div>
                      <label htmlFor="sub-company" className="mb-1.5 block text-xs font-semibold text-foreground-700">
                        品牌 / 机构
                      </label>
                      <input
                        id="sub-company"
                        name="company"
                        type="text"
                        placeholder="你所在的品牌或机构"
                        className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                      />
                    </div>
                    <div>
                      <label htmlFor="sub-focus" className="mb-1.5 block text-xs font-semibold text-foreground-700">
                        关注方向
                      </label>
                      <select
                        id="sub-focus"
                        name="focus"
                        defaultValue="合作前排查"
                        className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                      >
                        <option value="合作前排查">合作前排查</option>
                        <option value="续约评估">续约评估</option>
                        <option value="公关危机跟踪">公关危机跟踪</option>
                        <option value="品牌解约动态">品牌解约动态</option>
                      </select>
                    </div>
                  </div>

                  <div className="mt-4">
                    <label htmlFor="sub-note" className="mb-1.5 block text-xs font-semibold text-foreground-700">
                      备注（选填）
                    </label>
                    <textarea
                      id="sub-note"
                      name="note"
                      rows={3}
                      maxLength={500}
                      placeholder="可写下正在关注的合作对象或风险方向"
                      className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    ></textarea>
                  </div>

                  {/* anti-spam honeypot */}
                  <input
                    className="form-honey-field"
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {status === "error" && formError && (
                    <p className="mt-4 flex items-center gap-2 rounded-md bg-primary-50 px-3 py-2.5 text-xs text-primary-700">
                      <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center"></i>
                      {formError}
                    </p>
                  )}

                  <div className="mt-5 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-[11px] leading-relaxed text-foreground-500">
                      提交即表示你了解：本站信息仅用于风险参考，不构成对任何主体的定性。
                    </p>
                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                    >
                      {status === "submitting" ? (
                        <>
                          <i className="ri-loader-4-line w-4 h-4 flex items-center justify-center animate-spin"></i>
                          提交中
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-fill w-4 h-4 flex items-center justify-center"></i>
                          提交订阅
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}